import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAccount } from "wagmi";
import AdminESGList from "./AdminESGList";
import AdminESGDetail from "./AdminESGDetail";
import AdminDashboardStats from "./AdminDashboardStats";
import AdminLiquidityPanel from "./AdminLiquidityPanel";
import { adminApi } from "../../services/api";

/**
 * Componente principale per la sezione Admin
 * Gestisce l'autenticazione e il routing delle pagine admin
 */
const AdminPanel = () => {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Admin wallet address (stesso del ESGDashboard)
  const ADMIN_WALLET_ADDRESS = "0x8e46AF311F328FDa5B5F8c648BB91B6e2022D4a3".toLowerCase();
  
  const [adminKey, setAdminKey] = useState("");
  const [adminKeyInput, setAdminKeyInput] = useState("");
  const [adminKeyOk, setAdminKeyOk] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  // Verifica se l'utente è l'admin
  const isAdmin = isConnected && address?.toLowerCase() === ADMIN_WALLET_ADDRESS;

  // Carica admin key da sessionStorage se disponibile e verifica
  useEffect(() => {
    const savedKey = sessionStorage.getItem("admin_key");
    if (savedKey && isAdmin) {
      // Verifica la chiave salvata
      const verifyStoredKey = async () => {
        try {
          console.log("[AdminPanel] Verifying stored admin key...");
          await adminApi.verifyAdminKey(savedKey, address);
          console.log("[AdminPanel] Stored admin key is valid!");
          setAdminKey(savedKey);
          setAdminKeyOk(true);
        } catch (error) {
          console.error("[AdminPanel] Stored admin key is invalid:", error);
          // Rimuovi la chiave non valida
          sessionStorage.removeItem("admin_key");
          setAdminKey("");
          setAdminKeyOk(false);
        }
      };
      
      verifyStoredKey();
    }
  }, [isAdmin, address]);

  // Gestisce il login admin
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsVerifying(true);

    if (!isAdmin) {
      setErrorMessage("Only admin wallet can access this section");
      setIsVerifying(false);
      return;
    }

    if (!adminKeyInput.trim()) {
      setErrorMessage("Please enter admin key");
      setIsVerifying(false);
      return;
    }

    try {
      // Verifica l'admin key con il backend
      console.log("[AdminPanel] Verifying admin key...");
      await adminApi.verifyAdminKey(adminKeyInput, address);
      
      console.log("[AdminPanel] Admin key verified successfully!");
      
      // Salva la chiave
      setAdminKey(adminKeyInput);
      setAdminKeyOk(true);
      sessionStorage.setItem("admin_key", adminKeyInput);
      
      // Naviga alla lista se siamo sulla pagina di login
      if (location.pathname === "/admin" || location.pathname === "/admin/") {
        navigate("/admin/esg");
      }
    } catch (error) {
      console.error("[AdminPanel] Admin key verification failed:", error);
      
      // Mostra messaggio di errore appropriato
      if (error.status === 401) {
        setErrorMessage("Invalid admin key. Please check and try again.");
      } else if (error.message?.includes("REACT_APP_API")) {
        setErrorMessage("Backend API not configured. Please set REACT_APP_API_URL.");
      } else {
        setErrorMessage(error.message || "Failed to verify admin key. Please try again.");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Gestisce il logout admin
  const handleAdminLogout = () => {
    setAdminKey("");
    setAdminKeyInput("");
    setAdminKeyOk(false);
    sessionStorage.removeItem("admin_key");
    navigate("/admin");
  };

  // Se non è admin wallet, mostra errore
  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          <h2 style={styles.errorTitle}>Access Denied</h2>
          <p style={styles.errorMessage}>
            You must connect with the admin wallet to access this section.
          </p>
          {isConnected && (
            <p style={styles.errorMessage}>
              Connected: {address?.slice(0, 10)}...{address?.slice(-8)}
            </p>
          )}
          {!isConnected && (
            <p style={styles.errorMessage}>Please connect your wallet first.</p>
          )}
        </div>
      </div>
    );
  }

  // Se non ha inserito la chiave, mostra form login
  if (!adminKeyOk) {
    return (
      <div style={styles.container}>
        <div style={styles.loginCard}>
          <h2 style={styles.loginTitle}>Admin Panel</h2>
          <p style={styles.loginSubtitle}>
            Connected as Admin: {address?.slice(0, 10)}...{address?.slice(-8)}
          </p>

          {errorMessage && (
            <div style={styles.errorMessage}>{errorMessage}</div>
          )}

          <form onSubmit={handleAdminLogin} style={styles.loginForm}>
            <label style={styles.loginLabel}>Admin Key:</label>
            <input
              type="password"
              value={adminKeyInput}
              onChange={(e) => setAdminKeyInput(e.target.value)}
              placeholder="Enter admin key"
              style={styles.loginInput}
              autoFocus
              disabled={isVerifying}
            />

            <button 
              type="submit" 
              style={{
                ...styles.loginButton,
                opacity: isVerifying ? 0.6 : 1,
                cursor: isVerifying ? 'not-allowed' : 'pointer'
              }}
              disabled={isVerifying}
            >
              {isVerifying ? "Verifying..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Se autenticato, mostra il pannello admin
  return (
    <div style={styles.adminContainer}>
      {/* Header */}
      <div style={styles.adminHeader}>
        <div style={styles.adminHeaderLeft}>
          <h1 style={styles.adminTitle}>Admin Panel</h1>
          <span style={styles.adminWallet}>
            {address?.slice(0, 10)}...{address?.slice(-8)}
          </span>
        </div>
        <div style={styles.adminHeaderRight}>
          <button onClick={handleAdminLogout} style={styles.logoutButton}>
            Logout
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div style={styles.adminNav}>
        <button
          onClick={() => navigate("/admin/stats")}
          style={{
            ...styles.navButton,
            ...(location.pathname.includes("/admin/stats") ? styles.navButtonActive : {}),
          }}
        >
          📊 Dashboard Stats
        </button>
        <button
          onClick={() => navigate("/admin/esg")}
          style={{
            ...styles.navButton,
            ...(location.pathname.includes("/admin/esg") ? styles.navButtonActive : {}),
          }}
        >
          ESG Requests
        </button>
        <button
          onClick={() => navigate("/admin/liquidity")}
          style={{
            ...styles.navButton,
            ...(location.pathname.includes("/admin/liquidity") ? styles.navButtonActive : {}),
          }}
        >
          💧 Liquidity
        </button>
        <button
          onClick={() => navigate("/")}
          style={styles.navButton}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Routes */}
      <div style={styles.adminContent}>
        <Routes>
          <Route path="/" element={<AdminHome />} />
          <Route path="/stats" element={<AdminDashboardStats adminKey={adminKey} walletAddress={address} />} />
          <Route path="/esg" element={<AdminESGList adminKey={adminKey} walletAddress={address} />} />
          <Route path="/esg/requests/:id" element={<AdminESGDetail adminKey={adminKey} walletAddress={address} />} />
          <Route path="/liquidity" element={<AdminLiquidityPanel adminKey={adminKey} />} />
          <Route path="/liquidity/*" element={<AdminLiquidityPanel adminKey={adminKey} />} />
        </Routes>
      </div>
    </div>
  );
};

// Home page semplice per l'admin
const AdminHome = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.homeContainer}>
      <h2 style={styles.homeTitle}>Welcome to Admin Panel</h2>
      <p style={styles.homeSubtitle}>Select a section to manage:</p>

      <div style={styles.homeCards}>
        <div style={styles.homeCard} onClick={() => navigate("/admin/stats")}>
          <div style={styles.homeCardIcon}>📊</div>
          <h3 style={styles.homeCardTitle}>Dashboard Stats</h3>
          <p style={styles.homeCardDescription}>
            View ESG certification statistics, badge distribution, IPFS publishing rates
          </p>
        </div>
        <div style={styles.homeCard} onClick={() => navigate("/admin/esg")}>
          <div style={styles.homeCardIcon}>📋</div>
          <h3 style={styles.homeCardTitle}>ESG Requests</h3>
          <p style={styles.homeCardDescription}>
            Manage ESG questionnaire submissions, review answers, download attachments
          </p>
        </div>
        <div style={styles.homeCard} onClick={() => navigate("/admin/liquidity")}>
          <div style={styles.homeCardIcon}>💧</div>
          <h3 style={styles.homeCardTitle}>Liquidity Monitoring</h3>
          <p style={styles.homeCardDescription}>
            Monitor liquidity program status, incidents, flagged wallets and weekly statistics
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ecf0f1",
    padding: "20px",
  },
  errorBox: {
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "8px",
    boxShadow: "0 2px 20px rgba(0,0,0,0.1)",
    textAlign: "center",
    maxWidth: "500px",
  },
  errorTitle: {
    color: "#e74c3c",
    fontSize: "24px",
    marginBottom: "15px",
  },
  errorMessage: {
    color: "#7f8c8d",
    fontSize: "16px",
    marginBottom: "10px",
  },
  loginCard: {
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "8px",
    boxShadow: "0 2px 20px rgba(0,0,0,0.1)",
    maxWidth: "450px",
    width: "100%",
  },
  loginTitle: {
    color: "#2c3e50",
    fontSize: "28px",
    fontWeight: "600",
    marginBottom: "10px",
    textAlign: "center",
  },
  loginSubtitle: {
    color: "#7f8c8d",
    fontSize: "14px",
    marginBottom: "25px",
    textAlign: "center",
  },
  loginForm: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  loginLabel: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#2c3e50",
  },
  loginInput: {
    padding: "12px",
    border: "1px solid #bdc3c7",
    borderRadius: "4px",
    fontSize: "14px",
    color: "#2c3e50",
    backgroundColor: "white",
  },
  loginButton: {
    padding: "12px",
    backgroundColor: "#27ae60",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "10px",
  },
  adminContainer: {
    minHeight: "100vh",
    backgroundColor: "#ecf0f1",
  },
  adminHeader: {
    backgroundColor: "#2c3e50",
    color: "white",
    padding: "20px 30px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  adminHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  adminTitle: {
    fontSize: "24px",
    fontWeight: "600",
    margin: 0,
  },
  adminWallet: {
    fontSize: "14px",
    opacity: 0.8,
  },
  adminHeaderRight: {},
  logoutButton: {
    padding: "8px 16px",
    backgroundColor: "#e74c3c",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  adminNav: {
    backgroundColor: "#34495e",
    padding: "10px 30px",
    display: "flex",
    gap: "10px",
  },
  navButton: {
    padding: "10px 20px",
    backgroundColor: "transparent",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.3s",
  },
  navButtonActive: {
    backgroundColor: "#2c3e50",
  },
  adminContent: {
    padding: "20px",
  },
  homeContainer: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "40px 20px",
  },
  homeTitle: {
    fontSize: "32px",
    fontWeight: "600",
    color: "#2c3e50",
    marginBottom: "10px",
  },
  homeSubtitle: {
    fontSize: "18px",
    color: "#7f8c8d",
    marginBottom: "40px",
  },
  homeCards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },
  homeCard: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "transform 0.3s, box-shadow 0.3s",
    textAlign: "center",
  },
  homeCardIcon: {
    fontSize: "48px",
    marginBottom: "15px",
  },
  homeCardTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#2c3e50",
    marginBottom: "10px",
  },
  homeCardDescription: {
    fontSize: "14px",
    color: "#7f8c8d",
    lineHeight: "1.6",
  },
};

export default AdminPanel;
